import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, UserMinus, UserCog } from 'lucide-react';
import { formatUserRole } from '@/lib/utils';
import type { User } from '@/services/types';

type UserWithRole = User & {
  role: string;
};

interface UserTableColumnsProps {
  onSwitchRole: (user: UserWithRole) => void;
  onDeleteUser: (user: UserWithRole) => void;
  isAdmin: boolean;
}

export const createUserColumns = ({ onSwitchRole, onDeleteUser, isAdmin }: UserTableColumnsProps): ColumnDef<UserWithRole>[] => [
  {
    accessorKey: 'fullName',
    header: 'Name',
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className='flex items-center space-x-3'>
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-muted'>
            <span className='text-sm font-medium'>
              {user.fullName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <div className='font-medium'>{user.fullName || 'No name'}</div>
            <div className='text-sm text-muted-foreground'>{user.email}</div>
          </div>
        </div>
      );
    }
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => {
      const role = row.getValue<string>('role');
      
      return (
        <Badge variant={'secondary'}>
          {formatUserRole(role)}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'emailVerified',
    header: 'Email Verified',
    cell: ({ row }) => {
      const user = row.original;
      const isVerified = user.emailVerified && user.isActive;
      
      return (
        <Badge variant={isVerified ? 'success' : 'destructive'}>
          {isVerified ? 'Verified' : 'Unverified'}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Joined',
    cell: ({ row }) => {
      const date = row.getValue<Date>('createdAt');
      return (
        <div className='text-sm'>
          {new Date(date).toLocaleDateString()}
        </div>
      );
    }
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const user = row.original;
      
      if (!isAdmin) {
        return null;
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onSwitchRole(user)}>
              <UserCog className='mr-2 h-4 w-4' />
              Switch Role
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onDeleteUser(user)}
              className='text-red-600'
            >
              <UserMinus className='mr-2 h-4 w-4' />
              Delete User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
  }
];